import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth.ts';
import { useUserStore } from '@/stores/user.ts';

/**
 * Carries the page that sent the user into the MFA setup flow, so the flow can return there instead of dropping
 * them on the partner app. Shared by every screen in the flow: propagating it in one place and forgetting it in
 * the next is what makes these journeys dead-end.
 */
export const useOriginRedirect = () => {
	const route = useRoute();
	const router = useRouter();
	const authStore = useAuthStore();
	const userStore = useUserStore();

	const originRedirect = computed(() => (typeof route.query.redirect === 'string' ? route.query.redirect : ''));

	// A forced user must finish the whole email -> strong-method chain. Honouring the origin would let
	// SetupMfaConfirmation short-circuit that chain, and leaving a `setup-mfa-*` route while `mfa_forced` is set
	// trips the router's logout guard — so the redirect is dropped for them and the forced flow runs to its end.
	const forwardedQuery = computed(() => {
		if (!originRedirect.value || userStore.user.mfa_forced) {
			return undefined;
		}
		return { redirect: originRedirect.value };
	});

	// The TOTP branch can't carry `redirect`: SetupMfaConfirmation short-circuits on that key and would skip the
	// recovery-keys screen. `origin` is the same value under a key the confirmation ignores, forwarded along the
	// TOTP chain and consumed by SetupTotpSuccessRedirect. It inherits forwardedQuery's forced-user rule.
	const forwardedOrigin = computed(() => (forwardedQuery.value ? { origin: forwardedQuery.value.redirect } : undefined));

	// Read side of `origin`, for the screens that only pass it on.
	const carriedOrigin = computed(() => (typeof route.query.origin === 'string' ? route.query.origin : ''));

	const leaveFlow = () => {
		if (originRedirect.value) {
			router.replace({ name: originRedirect.value });
			return;
		}
		router.replace(authStore.targetUrl);
	};

	return { originRedirect, forwardedQuery, forwardedOrigin, carriedOrigin, leaveFlow };
};
